/**
 * Gestión de sedes (sucursales) del policlínico — área del tenant.
 * El administrador del policlínico crea y edita sus propias sedes sin
 * depender del panel de super-admin. Requiere permisos sede:read / sede:create.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Plus } from "lucide-react";
import {
  type Sede,
  type SedeCreate,
  type SedeUpdate,
  createSede,
  listSedes,
  updateSede,
} from "../api/tenants";
import { ApiError, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

export function SedesListPage() {
  const { me } = useAuth();
  const puedeCrear = me?.permisos.includes("sede:create") ?? false;
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Sede | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sedes"],
    queryFn: listSedes,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sedes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Sucursales del policlínico donde se atiende. Todo policlínico necesita al menos una.
          </p>
        </div>
        {puedeCrear && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4" />
            Nueva sede
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">Listado de sedes</span>
            {data && <span className="ml-auto text-xs text-slate-500">{data.length}</span>}
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando sedes…</div>
          )}
          {error && (
            <div className="px-6 py-4">
              <Alert>No se pudieron cargar las sedes.</Alert>
            </div>
          )}
          {data && data.length === 0 && !isLoading && (
            <div className="px-6 py-12 text-center">
              <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Aún no hay sedes registradas.</p>
              {puedeCrear && (
                <button
                  onClick={() => setCreateOpen(true)}
                  className="mt-3 text-sm text-[color:var(--color-link)] hover:text-[color:var(--color-link)] font-medium"
                >
                  Registrar la primera
                </button>
              )}
            </div>
          )}
          {data && data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <Th>Nombre</Th>
                    <Th>Ubigeo</Th>
                    <Th>Dirección</Th>
                    <Th>Categoría</Th>
                    <Th>Estado</Th>
                    <Th> </Th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="px-6 py-3 font-medium text-slate-900">{s.nombre}</td>
                      <td className="px-6 py-3 font-mono text-xs text-slate-600">{s.ubigeo}</td>
                      <td className="px-6 py-3 text-slate-600">{s.direccion}</td>
                      <td className="px-6 py-3">
                        <Badge>{s.categoria_minsa}</Badge>
                      </td>
                      <td className="px-6 py-3">
                        <Badge variant={s.activo ? "success" : "danger"}>
                          {s.activo ? "Activa" : "Inactiva"}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-right">
                        {puedeCrear && (
                          <button
                            onClick={() => setEditing(s)}
                            className="text-[color:var(--color-link)] hover:text-[color:var(--color-link)] text-xs font-medium"
                          >
                            Editar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <CreateSedeDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditSedeDialog sede={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

const EMPTY: SedeCreate = {
  nombre: "",
  ubigeo: "",
  direccion: "",
  telefono: "",
  categoria_minsa: "I-1",
};

function CreateSedeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SedeCreate>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: SedeCreate) => createSede(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sedes"] });
      onClose();
      setForm(EMPTY);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        setError("Datos inválidos. Verifica ubigeo (6 dígitos) y categoría (I-1 a I-4).");
      } else {
        setError(formatApiError(err, "No se pudo crear la sede."));
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ ...form, telefono: form.telefono || undefined });
  }

  return (
    <Dialog open={open} onClose={onClose} title="Nueva sede">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field label="Nombre" htmlFor="s_nombre">
          <Input
            id="s_nombre"
            value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            placeholder="Sede Miraflores"
            required
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Ubigeo INEI" htmlFor="s_ubigeo" hint="6 dígitos.">
            <Input
              id="s_ubigeo"
              value={form.ubigeo}
              onChange={(e) => setForm({ ...form, ubigeo: e.target.value })}
              placeholder="150122"
              maxLength={6}
              inputMode="numeric"
              required
            />
          </Field>
          <Field label="Categoría MINSA" htmlFor="s_cat">
            <select
              id="s_cat"
              value={form.categoria_minsa}
              onChange={(e) => setForm({ ...form, categoria_minsa: e.target.value })}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            >
              <option value="I-1">I-1</option>
              <option value="I-2">I-2</option>
              <option value="I-3">I-3</option>
              <option value="I-4">I-4</option>
            </select>
          </Field>
        </div>
        <Field label="Dirección" htmlFor="s_dir">
          <Input
            id="s_dir"
            value={form.direccion}
            onChange={(e) => setForm({ ...form, direccion: e.target.value })}
            placeholder="Av. Larco 1234"
            required
          />
        </Field>
        <Field label="Teléfono" htmlFor="s_tel" hint="Opcional.">
          <Input
            id="s_tel"
            value={form.telefono ?? ""}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            placeholder="01-2345678"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Crear sede
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditSedeDialog({ sede, onClose }: { sede: Sede | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SedeUpdate>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sede) {
      setForm({
        nombre: sede.nombre,
        direccion: sede.direccion,
        telefono: sede.telefono ?? "",
        categoria_minsa: sede.categoria_minsa,
        activo: sede.activo,
      });
      setError(null);
    }
  }, [sede]);

  const mutation = useMutation({
    mutationFn: (payload: SedeUpdate) => updateSede(sede!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sedes"] });
      onClose();
    },
    onError: (err) => setError(formatApiError(err, "No se pudo guardar.")),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ ...form, telefono: form.telefono || null });
  }

  return (
    <Dialog open={sede !== null} onClose={onClose} title="Editar sede" description={sede?.nombre}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field label="Nombre" htmlFor="e_nombre">
          <Input
            id="e_nombre"
            value={form.nombre ?? ""}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            required
          />
        </Field>
        <Field label="Categoría MINSA" htmlFor="e_cat">
          <select
            id="e_cat"
            value={form.categoria_minsa ?? "I-1"}
            onChange={(e) => setForm({ ...form, categoria_minsa: e.target.value })}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="I-1">I-1</option>
            <option value="I-2">I-2</option>
            <option value="I-3">I-3</option>
            <option value="I-4">I-4</option>
          </select>
        </Field>
        <Field label="Dirección" htmlFor="e_dir">
          <Input
            id="e_dir"
            value={form.direccion ?? ""}
            onChange={(e) => setForm({ ...form, direccion: e.target.value })}
          />
        </Field>
        <Field label="Teléfono" htmlFor="e_tel">
          <Input
            id="e_tel"
            value={form.telefono ?? ""}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.activo ?? true}
            onChange={(e) => setForm({ ...form, activo: e.target.checked })}
            className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
          />
          Sede activa
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
